import React, {useEffect} from 'react';
import {useCallback, useMemo, useState} from 'react';
import {useDocumentClickListener} from '../../../hooks';
import {Icon} from '../../icons';
import {IconNames} from '../../icons/types';
import {Input, LeftEnhancer} from '../../input';
import {Layout} from '../../layout';
import {Ruler} from '../../ruler';
import {Spacer} from '../../spacer';
import {Dropdown} from '../Dropdown';
import {SimpleDropdownItem} from './SimpleDropdownItem';
import {SimpleDropdownProps} from '../types';
import {BadgeOrIcon} from './BadgeOrIcon';

const EmptyIcons: IconNames[] = [];
type ItemWithID = {id: number; value: string};

export const Searchable: React.FC<Omit<SimpleDropdownProps, 'readonly'>> = ({
  items,
  favourites,
  icons = EmptyIcons,
  placeholder,
  selectedIndex,
  onSelectedIndexChange,
  onRenderBadge,
}) => {
  // State
  const [expanded, setExpanded] = useState<boolean>(false);
  const [query, setQuery] = useState<string>();
  const [selected, setSelected] = useState<string>();

  // Make sure we have an "id" for the dropdown
  const itemsWithId = useMemo(
    (): ItemWithID[] => items.map((itm, index) => ({id: index, value: itm})),
    [items],
  );

  // Favourites
  const favouriteIndicies = useMemo(() => {
    // Only return favourites when query is empty
    return !query ? favourites?.map(f => itemsWithId[f].id) : [];
  }, [favourites, itemsWithId, query]);

  // Handlers
  const handleSelection = useCallback(
    (index: number) => {
      setExpanded(false);
      setSelected(itemsWithId[index].value);
      setQuery('');
      onSelectedIndexChange && onSelectedIndexChange(index);
    },
    [itemsWithId, onSelectedIndexChange],
  );

  const toggleExpanded = useCallback(() => setExpanded(p => !p), []);
  const closeDrowdown = useCallback(() => {
    setExpanded(false);
    setQuery('');
  }, []);

  // Filter item
  const isVisibleItem = useCallback(
    (item: ItemWithID, q: string | undefined) => {
      if (q) {
        return item.value.toLocaleLowerCase().includes(q.toLocaleLowerCase());
      }
      return true;
    },
    [],
  );

  // render item
  const renderItem = useCallback(
    (_, index: number) => {
      // This is where we filter out searchable items
      if (query) {
        if (!isVisibleItem(itemsWithId[index], query)) {
          return null;
        }
      }
      return (
        <SimpleDropdownItem
          index={index}
          title={items[index]}
          badge={
            <BadgeOrIcon
              index={index}
              onRenderBadge={onRenderBadge}
              icons={icons}
            />
          }
          isActive={index === selectedIndex}
          onSelectedIndexChange={handleSelection}
        />
      );
    },
    [
      query,
      items,
      onRenderBadge,
      icons,
      selectedIndex,
      handleSelection,
      isVisibleItem,
      itemsWithId,
    ],
  );

  // When we blur the input we should look to see if we have a selected index
  // and replace query with that one
  const handleInputBlur = useCallback(() => {
    if (!expanded && selectedIndex !== undefined && selectedIndex !== -1) {
      setQuery('');
      setSelected(itemsWithId[selectedIndex].value);
    }
  }, [itemsWithId, expanded, selectedIndex]);

  const renderSeparator = useCallback(() => <Ruler padding="6" />, []);

  // Handle dismissal of menu when clicking outside menu
  useDocumentClickListener(expanded, closeDrowdown);

  // Handle search - we should remove the selection whenever a search is
  // entered.
  useEffect(() => {
    if (query) {
      setSelected('');
      setQuery(query);
      onSelectedIndexChange && onSelectedIndexChange(undefined);
    }
  }, [query, items.length, onSelectedIndexChange]);

  return (
    <Layout vertical height="44">
      <Layout flex height={44}>
        <Input
          flex
          clear
          onChange={setQuery}
          onBlur={handleInputBlur}
          rightIcon="arrowDownSmall"
          rightOnPress={toggleExpanded}
          placeholder={placeholder}
          value={query || selected || ''}>
          <LeftEnhancer>
            {query || selectedIndex === undefined || selectedIndex === -1 ? (
              <>
                <Icon name="search" size="24" />
                <Spacer width="10" />
              </>
            ) : (
              <BadgeOrIcon
                index={selectedIndex}
                onRenderBadge={onRenderBadge}
                icons={icons}
              />
            )}
          </LeftEnhancer>
        </Input>
      </Layout>
      <Layout>
        <Dropdown
          state={expanded || query ? 'expanded' : 'collapsed'}
          items={itemsWithId}
          filterOutFavourites
          renderFavouritesSeparator={renderSeparator}
          favourites={favouriteIndicies}
          renderItemContent={renderItem}
        />
      </Layout>
    </Layout>
  );
};
